<script>
  // Step 1 of Sources: assess the source and label it, on one surface.
  //
  // The table is a review surface, not a form. It reads left to right as the
  // pipeline — what the column holds, then the three things you can decide about
  // it, then what it publishes — and every control lives in the per-column
  // editor. Most columns are already answered by their catalog term, so the
  // screen should read as "check these", with editing one deliberate click away.
  //
  // Assess used to be a separate screen. Splitting them only worked on a blank
  // slate: the recurring task is checking an updated export against a mapping
  // that already exists, and that question — what do my transforms do to this
  // new file — needs the profile and the result in the same row.
  import DisclosureStepper from '../components/DisclosureStepper.svelte';
  import Panel from '../components/Panel.svelte';
  import Section from '../components/Section.svelte';
  import Button from '../components/Button.svelte';
  import Alert from '../components/Alert.svelte';
  import Drawer from '../components/Drawer.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import ColumnEditor from '../components/ColumnEditor.svelte';
  import { HELP } from '../lib/help.js';
  import { disclosureDraft, loadCsv, markDirty, ensureDraft } from '../lib/disclosureDraft.svelte.js';
  import { nav } from '../lib/nav.svelte.js';
  import { onMount } from 'svelte';
  import {
    TRANSFORM_LABELS, transformSteps, overridesFor, sortBySourceOrder,
    traceColumn, reconcile, recordSummary, columnProblems, labelGate,
  } from '../../../lib/disclosure_core.js';

  // Which column is open, and which of its three facets. Opening from a cell goes
  // straight to that cell's question rather than the whole column.
  let editing = $state(null);
  let facet = $state(null);
  let error = $state(null);
  onMount(() => ensureDraft(nav.params.source).catch((e) => { error = e.message; }));
  const open = (column, which) => { editing = column; facet = which; };

  const draft = $derived(disclosureDraft);
  const authored = $derived((draft.mapping?.columns ?? []).filter((c) => c.record !== 'shared'));
  const housekeeping = $derived((draft.mapping?.columns ?? []).filter((c) => c.record === 'shared'));

  // Two different questions, and conflating them was a bug: whether a file is
  // loaded decides what can be said about the *columns*, while whether it has
  // rows decides what can be shown as *values*. A file with a header row and no
  // data answers the first and not the second.
  const hasCsv = $derived(draft.headers.length > 0);
  const hasRows = $derived(draft.rows.length > 0);
  const row = $derived(hasRows ? draft.rows[Math.min(Math.max(draft.rowIndex, 1), draft.rows.length) - 1] : null);
  const state = $derived(reconcile(draft.mapping, draft.headers));
  const records = $derived(recordSummary(draft.mapping));

  async function choose(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Replacing the file is the point of re-assessing, and it is safe: only the
    // rows, headers and profiles change. The mapping you already wrote stays.
    try { error = null; loadCsv(file.name, await file.text()); } catch (x) { error = x.message; }
  }

  // Grouped by record, because the record is now a consequence of the term rather
  // than a per-row choice — the group heading is where the analyst reads it.
  // Columns with no term yet have no record either, and gather in their own group.
  const UNLABELLED = 'Not yet labelled';
  const groups = $derived.by(() => {
    const byRecord = new Map(Object.keys(draft.mapping?.records ?? {}).map((id) => [id, []]));
    for (const column of authored) {
      const key = byRecord.has(column.record) ? column.record : UNLABELLED;
      if (!byRecord.has(key)) byRecord.set(key, []);
      byRecord.get(key).push(column);
    }
    return [...byRecord].filter(([, columns]) => columns.length);
  });

  const profileOf = (source) => draft.profiles.find((p) => p.name === source) ?? null;
  const isMissing = (source) => hasCsv && !draft.headers.includes(source);

  const raw = (column) => (row ? String(row[column.source] ?? '') : '');
  const published = (column) => {
    if (!row) return '';
    const trace = traceColumn(column, row);
    return trace.error ? '⚠ ' + trace.error : trace.values.join(' · ');
  };

  const chainLabel = (column) => transformSteps(column.transform).map((s) => TRANSFORM_LABELS[s.fn] ?? s.fn).join(' → ');
  const problems = (column) => columnProblems(column, draft.headers);
  const incomplete = $derived(authored.filter((c) => problems(c).length));
  // One gate, replacing the two the split flow had: the file must be accounted
  // for in both directions and every kept column must be answerable. It lives in
  // core because the stepper offers the same move and must refuse it identically.
  const gate = $derived(labelGate(draft));

  function dropNew(header) {
    if (!draft.mapping.drop.includes(header)) draft.mapping.drop.push(header);
    sortBySourceOrder(draft.mapping, draft.headers);
    markDirty();
  }

  function keepColumn(source) {
    draft.mapping.drop.splice(draft.mapping.drop.indexOf(source), 1);
    // Nothing is guessed: a restored column has no term until the analyst picks
    // one, and until then it has no record either, because the record follows the
    // term. It sits in its own group and blocks the step gate until answered.
    draft.mapping.columns.push({ source, record: null, target: '', term: '', transform: { fn: 'direct' },
      identifies: 'no', sensitivity: 'personal', treatment: 'isolate' });
    sortBySourceOrder(draft.mapping, draft.headers);
    markDirty();
    // `$state` wraps an inserted plain object in its deep reactive proxy. Open
    // the stored object, not the pre-insertion value, or drawer edits disappear
    // when it closes while sibling mapping mutations appear to persist.
    open(draft.mapping.columns.find((column) => column.source === source && column.record !== 'shared'), 'term');
  }

  function dropColumn(column) {
    draft.mapping.columns.splice(draft.mapping.columns.indexOf(column), 1);
    if (!draft.mapping.drop.includes(column.source)) draft.mapping.drop.push(column.source);
    sortBySourceOrder(draft.mapping, draft.headers);
    editing = null;
    markDirty();
  }

  // A record type is never added or removed here — it exists because a column's
  // term lands in it. The only exception is a leftover with no columns at all,
  // which can be cleared away because nothing depends on it.
  function removeRecord(id) {
    if (records.find((r) => r.id === id)?.columns.length) return;
    delete draft.mapping.records[id];
    markDirty();
  }
  function setContainer(id, container) {
    draft.mapping.records[id].container = container;
    markDirty();
  }
</script>

<DisclosureStepper active={0} />
<HelpDrawer {...HELP['disclosure/label']} />
<h1 class="wf2-title wf2-title-level-1">Assess and label the source</h1>
{#if draft.loaded}<p class="wf2-caption">Active source: <strong>{draft.connector?.sourceLabel ?? draft.sourceId}</strong> <span class="mono">{draft.sourceId}</span></p>{/if}
<p class="wf2-subtitle">
  Look at what each column actually holds, then say what it is. The record it belongs to and how it
  may be published follow from the shared term you choose — change them only where you disagree.
  The raw rows stay in this browser.
</p>

{#if !draft.loaded}
  <Alert title="Choose a source first"><Button to="disclosure">Open sources</Button></Alert>
{:else}
  <Section title="Source file">
    <Panel>
      <input class="wf2-control" type="file" accept=".csv,text/csv" onchange={choose} />
      {#if draft.fileName}
        <p class="wf2-hint">{draft.fileName} · {draft.rows.length} rows · {draft.headers.length} columns</p>
      {:else}
        <p class="wf2-hint">
          Without a file you can still read and change the labels below — you just won't see values
          or results. Load a CSV to check what your transforms do to it.
        </p>
      {/if}
    </Panel>
  </Section>

  {#if error}<Alert title="Could not read CSV" variant="danger"><p>{error}</p></Alert>{/if}

  {#if state.unaccounted.length}
    <Alert title="{state.unaccounted.length} {state.unaccounted.length === 1 ? 'column is' : 'columns are'} in the file but not in your mapping" variant="warning">
      <ul>
        {#each state.unaccounted as header}
          <li>
            {header} — {profileOf(header)?.distinct ?? 0} different values
            <Button variant="ghost" onclick={() => dropNew(header)}>Don't publish it</Button>
            <Button variant="ghost" onclick={() => keepColumn(header)}>Label it</Button>
          </li>
        {/each}
      </ul>
    </Alert>
  {/if}

  {#if state.missing.length}
    <Alert title="{state.missing.length} {state.missing.length === 1 ? 'column is' : 'columns are'} missing from the loaded file" variant="warning">
      <p>
        Your mapping expects {state.missing.map((s) => `“${s}”`).join(', ')}, which this file does not
        have. Left as is they publish nothing at all. Rename them in the export, or stop publishing them.
      </p>
    </Alert>
  {/if}

  {#if incomplete.length}
    <Alert title="{incomplete.length} {incomplete.length === 1 ? 'column needs' : 'columns need'} attention" variant="warning">
      <ul>
        {#each incomplete as column}
          <li>
            <button class="wf2-link" onclick={() => open(column, column.term ? 'transform' : 'term')}>{column.source}</button>
            — {problems(column)[0]}
          </li>
        {/each}
      </ul>
    </Alert>
  {/if}

  <Panel>
    {#if hasRows}
      <div class="wf2-row" style="gap:12px;align-items:center">
        <label class="wf2-field">
          Inspect row
          <input class="wf2-control" type="number" min="1" max={draft.rows.length} bind:value={draft.rowIndex} />
        </label>
        <span class="wf2-caption">
          of {draft.rows.length}. Flip through rows to see the range of values, and what each one
          becomes once your transforms run.
        </span>
      </div>
    {/if}
    <div class="wf2-tablewrap" style="overflow-x:auto">
      <table class="wf2-table">
        <thead>
          <tr>
            <th>Column</th>
            <th>In this row</th>
            <th>Shared term</th>
            <th>Identifies? · Sensitivity → Treatment</th>
            <th>Transform</th>
            <th>Published</th>
          </tr>
        </thead>
        <tbody>
          {#each groups as [record, columns]}
            <tr class="group-head">
              <td colspan="6">{record}<span class="group-count">{columns.length} columns</span></td>
            </tr>
            {#each columns as column}
              <tr>
                <td>
                  {column.source}
                  {#if isMissing(column.source)}
                    <span class="tag warn">not in file</span>
                  {:else if profileOf(column.source)}
                    <span class="col-stats">
                      {profileOf(column.source).distinct} different{#if profileOf(column.source).blank} · {profileOf(column.source).blank} blank{/if}
                    </span>
                  {/if}
                </td>
                <td class="mono">
                  {#if !row}—{:else if raw(column).trim() === ''}<span class="wf2-caption">blank in this row</span>{:else}{raw(column)}{/if}
                </td>
                <td>
                  <button class="cell-edit" onclick={() => open(column, 'term')}
                          aria-label="Change the shared term for {column.source}"
                  ><span class="mono">{column.term || '—'}</span>{#if column.manualTerm}<span class="wf2-caption"> by hand</span>{/if}<span class="cell-edit-suffix">edit</span></button>
                </td>
                <td>
                  <button class="cell-edit" onclick={() => open(column, 'labels')}
                          aria-label="Change the labels for {column.source}"
                  >{column.identifies} · {column.sensitivity} → {column.treatment}{#if overridesFor(column, draft.catalog).posture}<span class="tag">changed</span>{/if}{#if overridesFor(column, draft.catalog).treatment}<span class="tag">forced</span>{/if}<span class="cell-edit-suffix">edit</span></button>
                </td>
                <td>
                  <button class="cell-edit" onclick={() => open(column, 'transform')}
                          aria-label="Change the transform for {column.source}"
                  ><span class="wf2-caption">{chainLabel(column)}</span><span class="cell-edit-suffix">edit</span></button>
                </td>
                <td class="mono">{published(column) || '—'}</td>
              </tr>
            {/each}
          {/each}
        </tbody>
      </table>
    </div>
    <p class="wf2-hint">
      “Identifies?” and “Sensitivity” are your two labels; treatment is what they imply — drop, coarsen,
      isolate, or open. A column marked <span class="tag">changed</span> disagrees with the shared catalog,
      and that disagreement is saved with the mapping.
    </p>
  </Panel>

  <details class="drop-list">
    <summary>
      Not published
      <span class="group-count">{draft.mapping.drop.length} of {draft.mapping.drop.length + authored.length} columns</span>
    </summary>
    <Panel>
      <p class="wf2-caption">These stay in the raw record and are never published.</p>
      <div class="wf2-tablewrap">
        <table class="wf2-table">
          <tbody>
            {#each draft.mapping.drop as source}
              <tr>
                <td>{source}</td>
                <td class="wf2-caption">
                  {#if isMissing(source)}not in the loaded file{:else}{profileOf(source)?.distinct ?? 0} different values{/if}
                </td>
                <td><Button variant="ghost" onclick={() => keepColumn(source)}>Keep and label</Button></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </Panel>
  </details>

  <details class="drop-list">
    <summary>Record types<span class="group-count">{records.length}</span></summary>
    <Panel>
      <p class="wf2-caption">
        A record type is here because some column's shared term belongs to it — you don't choose them
        directly. The container is where the connector writes them in your Pod.
      </p>
      <div class="wf2-tablewrap">
        <table class="wf2-table">
          <thead><tr><th>Record type</th><th>Holds</th><th>Container</th><th></th></tr></thead>
          <tbody>
            {#each records as record}
              <tr>
                <td class="mono">{record.id}</td>
                <td class="wf2-caption">
                  {#if record.columns.length}{record.columns.map((c) => c.source).join(', ')}{:else}no columns{/if}
                </td>
                <td>
                  <input class="wf2-control" aria-label="Container for {record.id}" value={record.container}
                         oninput={(e) => setContainer(record.id, e.currentTarget.value)} />
                </td>
                <td>
                  {#if !record.columns.length}
                    <Button variant="ghost" onclick={() => removeRecord(record.id)}>Remove</Button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </Panel>
  </details>

  <details class="drop-list">
    <summary>Added by the connector<span class="group-count">{housekeeping.length}</span></summary>
    <Panel>
      <p class="wf2-caption">
        Housekeeping written to every record — the publishing institution and the time period.
        Not yours to label.
      </p>
      <div class="wf2-tablewrap">
        <table class="wf2-table">
          <thead><tr><th>Term</th><th>From</th><th>Transform</th></tr></thead>
          <tbody>
            {#each housekeeping as column}
              <tr><td class="mono">{column.target}</td><td>{column.source}</td><td class="wf2-caption">{chainLabel(column)}</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </Panel>
  </details>

  <div class="wf2-row" style="gap:12px">
    <Button variant="primary" to={`disclosure/aggregates?source=${encodeURIComponent(draft.sourceId)}`} disabled={gate.blocked}>Next: build cubes</Button>
    <Button to="disclosure">Back</Button>
  </div>
  {#if gate.blocked}<p class="wf2-hint">{gate.reason}</p>{/if}
{/if}

<Drawer
  title={editing ? editing.source : ''}
  open={Boolean(editing)}
  width={620}
  onclose={() => (editing = null)}
>
  {#if editing}
    <!-- Keyed so opening a different column starts its editor fresh rather than
         inheriting the last column's open pickers and expanded sections. -->
    {#key `${editing.source}#${facet}`}
    <ColumnEditor
      column={editing}
      {facet}
      catalog={draft.catalog}
      mapping={draft.mapping}
      rows={draft.rows}
      headers={draft.headers}
      profile={profileOf(editing.source)}
      onchange={markDirty}
      ondrop={() => dropColumn(editing)}
      onclose={() => (editing = null)}
    />
    {/key}
  {/if}
</Drawer>
